import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { SEOHead } from '../components/seo/SEOHead';
import { ArrowRight, ArrowLeft, CheckCircle, Shield, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  { id: 1, title: 'Service Interest', description: 'Select the service you need' },
  { id: 2, title: 'Your Reason', description: 'Why are you seeking this service?' },
  { id: 3, title: 'Contact Information', description: 'How can we reach you?' },
];

const services = [
  { value: 'record_suspension', label: 'Record Suspension', description: 'Clear your criminal record under the Criminal Records Act' },
  { value: 'us_entry_waiver', label: 'U.S. Entry Waiver', description: 'Gain legal entry to the United States despite inadmissibility' },
  { value: 'international_filings', label: 'International Filings', description: 'Document authentication for international use' },
];

const reasons = [
  { value: 'travel', label: 'Travel Related', description: 'Need to travel internationally' },
  { value: 'employment', label: 'Employment Related', description: 'Required for work or job application' },
  { value: 'personal_clarity', label: 'Personal Clarity', description: 'Want to understand your options' },
];

export default function Eligibility() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    service_interest: '',
    reason: '',
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    consent: false,
    honeypot: '',
  });
  const [errors, setErrors] = useState({});

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1 && !formData.service_interest) {
      newErrors.service_interest = 'Please select a service';
    }
    
    if (step === 2 && !formData.reason) {
      newErrors.reason = 'Please select a reason';
    }
    
    if (step === 3) {
      if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
      if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
      if (!formData.consent) newErrors.consent = 'You must consent to be contacted';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/eligibility`, formData);
      
      if (response.data.blocked) {
        toast.error(response.data.message || 'Unable to process your request. Please try again later.');
      } else {
        navigate('/thank-you', { 
          state: { 
            submissionId: response.data.submission_id,
            serviceType: formData.service_interest 
          } 
        });
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead page="eligibility" />
      
      <div data-testid="eligibility-page">
        {/* Hero Section */}
        <section className="bg-[hsl(215,50%,23%)] text-white py-10 md:py-14">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-2xl md:text-3xl font-bold mb-3 text-white">Eligibility Assessment</h1>
            <p className="text-white/80">
              Complete the form below to begin your eligibility review. Your information is secure.
            </p>
          </div>
        </section>

      {/* Progress Steps */}
      <section className="bg-[hsl(210,20%,96%)] py-6 border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div 
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                      currentStep > step.id 
                        ? 'bg-green-600 text-white' 
                        : currentStep === step.id 
                          ? 'bg-[hsl(215,50%,23%)] text-white' 
                          : 'bg-gray-300 text-gray-600'
                    }`}
                  >
                    {currentStep > step.id ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      step.id
                    )}
                  </div>
                  <span className="text-xs text-gray-600 mt-2 hidden md:block">{step.title}</span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`h-1 w-16 md:w-24 mx-2 md:mx-4 ${
                    currentStep > step.id ? 'bg-green-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Container */}
      <section className="gov-section bg-white">
        <div className="max-w-2xl mx-auto px-4 md:px-6">
          <div className="gov-panel">
            {/* Hidden honeypot field */}
            <input
              type="text"
              name="website"
              value={formData.honeypot}
              onChange={(e) => setFormData({ ...formData, honeypot: e.target.value })}
              style={{ display: 'none' }}
              tabIndex={-1}
              autoComplete="off"
            />

            {/* Step 1: Service Interest */}
            {currentStep === 1 && (
              <div className="space-y-6" data-testid="step-1">
                <div>
                  <h2 className="text-xl font-bold text-[hsl(215,50%,23%)] mb-1">Select Your Service</h2>
                  <p className="text-gray-600 text-sm">Which service are you interested in?</p>
                </div>
                
                <RadioGroup
                  value={formData.service_interest}
                  onValueChange={(value) => setFormData({ ...formData, service_interest: value })}
                  className="space-y-3"
                >
                  {services.map((service) => (
                    <div key={service.value}>
                      <label
                        className={`flex items-start p-4 border rounded-sm cursor-pointer transition-all ${
                          formData.service_interest === service.value
                            ? 'border-[hsl(215,50%,23%)] bg-[hsl(210,20%,96%)]'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                        data-testid={`service-${service.value}`}
                      >
                        <RadioGroupItem value={service.value} className="mt-1" />
                        <div className="ml-3">
                          <div className="font-semibold text-[hsl(215,50%,23%)]">{service.label}</div>
                          <div className="text-gray-600 text-sm mt-1">{service.description}</div>
                        </div>
                      </label>
                    </div>
                  ))}
                </RadioGroup>
                
                {errors.service_interest && (
                  <p className="text-red-600 text-sm flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    {errors.service_interest}
                  </p>
                )}
              </div>
            )}

            {/* Step 2: Reason */}
            {currentStep === 2 && (
              <div className="space-y-6" data-testid="step-2">
                <div>
                  <h2 className="text-xl font-bold text-[hsl(215,50%,23%)] mb-1">Your Reason</h2>
                  <p className="text-gray-600 text-sm">Why are you seeking this service?</p>
                </div>
                
                <RadioGroup
                  value={formData.reason}
                  onValueChange={(value) => setFormData({ ...formData, reason: value })}
                  className="space-y-3"
                >
                  {reasons.map((reason) => (
                    <div key={reason.value}>
                      <label
                        className={`flex items-start p-4 border rounded-sm cursor-pointer transition-all ${
                          formData.reason === reason.value
                            ? 'border-[hsl(215,50%,23%)] bg-[hsl(210,20%,96%)]'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                        data-testid={`reason-${reason.value}`}
                      >
                        <RadioGroupItem value={reason.value} className="mt-1" />
                        <div className="ml-3">
                          <div className="font-semibold text-[hsl(215,50%,23%)]">{reason.label}</div>
                          <div className="text-gray-600 text-sm mt-1">{reason.description}</div>
                        </div>
                      </label>
                    </div>
                  ))}
                </RadioGroup>
                
                {errors.reason && (
                  <p className="text-red-600 text-sm flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    {errors.reason}
                  </p>
                )}
              </div>
            )}

            {/* Step 3: Contact Information */}
            {currentStep === 3 && (
              <div className="space-y-6" data-testid="step-3">
                <div>
                  <h2 className="text-xl font-bold text-[hsl(215,50%,23%)] mb-1">Contact Information</h2>
                  <p className="text-gray-600 text-sm">How can we reach you?</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name" className="gov-label">First Name</Label>
                    <Input
                      id="first_name"
                      type="text"
                      placeholder="Enter your first name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="gov-input"
                      data-testid="input-first-name"
                    />
                    {errors.first_name && (
                      <p className="text-red-600 text-xs">{errors.first_name}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="last_name" className="gov-label">Last Name</Label>
                    <Input
                      id="last_name"
                      type="text"
                      placeholder="Enter your last name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="gov-input"
                      data-testid="input-last-name"
                    />
                    {errors.last_name && (
                      <p className="text-red-600 text-xs">{errors.last_name}</p>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone" className="gov-label">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="gov-input"
                    data-testid="input-phone"
                  />
                  {errors.phone && (
                    <p className="text-red-600 text-xs">{errors.phone}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="gov-label">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="gov-input"
                    data-testid="input-email"
                  />
                  {errors.email && (
                    <p className="text-red-600 text-xs">{errors.email}</p>
                  )}
                </div>
                
                <div className="flex items-start space-x-3 pt-4 border-t border-gray-200">
                  <Checkbox
                    id="consent"
                    checked={formData.consent}
                    onCheckedChange={(checked) => setFormData({ ...formData, consent: checked })}
                    className="mt-1"
                    data-testid="checkbox-consent"
                  />
                  <Label htmlFor="consent" className="text-gray-600 text-sm leading-relaxed cursor-pointer">
                    I consent to being contacted by Canadian Document Services Inc. regarding 
                    my eligibility review and related service communication.
                  </Label>
                </div>
                {errors.consent && (
                  <p className="text-red-600 text-xs">{errors.consent}</p>
                )}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
              {currentStep > 1 ? (
                <Button 
                  variant="outline" 
                  onClick={handleBack}
                  className="gov-btn-secondary"
                  data-testid="btn-back"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              ) : (
                <div />
              )}
              
              {currentStep < 3 ? (
                <Button 
                  onClick={handleNext}
                  className="gov-btn-primary"
                  data-testid="btn-next"
                >
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="gov-btn-primary"
                  data-testid="btn-submit"
                >
                  {isSubmitting ? 'Submitting...' : 'Begin Assessment'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Security Notice */}
          <div className="flex items-center justify-center gap-2 mt-6 text-gray-500 text-sm">
            <Shield className="h-4 w-4" />
            <span>Your information is secure and encrypted</span>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
